<script setup>
import { reactive, ref, watch, defineProps, defineEmits, computed, onMounted } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minValue, minLength } from '@vuelidate/validators';
import Swal from 'sweetalert2';
import api from '@/api';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';


const props = defineProps({
  visible: Boolean,
  modelValue: Object,
  isEdit: Boolean,
  onSave: Function
});
const duraciones = ref([]);
const emit = defineEmits(['update:visible']);

const form = reactive({
  nombre: '',
  descripcion: '',
  cantidad: 1,
  duracion_id: null,
  tipo_vencimiento: 'tiempo',
  limite_asistencias: null,
  precio: 0,
});

const duracionSeleccionada = computed(() => duraciones.value.find((d) => d.id === form.duracion_id) || null);
const esVariable = computed(() => duracionSeleccionada.value?.modo === 'variable');
const esVisitas = computed(() => duracionSeleccionada.value?.modo === 'visitas');

const rules = computed(() => ({
  nombre: { required, minLength: minLength(3) },
  descripcion: { required },
  duracion_id: { required },
  tipo_vencimiento: { required },
  precio: { required, minValue: minValue(0.01) },
  cantidad: esVariable.value ? { required, minValue: minValue(1) } : {},
  limite_asistencias: esVisitas.value
    ? { required, minValue: minValue(1) }
    : { minValue: minValue(0) },
}));

const v$ = useVuelidate(rules, form);

const isEdit = computed(() => !!props.modelValue?.id);

const previewVigencia = computed(() => {
  const d = duracionSeleccionada.value;
  if (!d) return '';
  if (d.modo === 'variable') return `${form.cantidad || 1} día(s) de temporada (fecha_fin = inicio + cantidad - 1)`;
  if (d.modo === 'visitas') return `Sin fecha (vence por ${form.limite_asistencias || '—'} visitas)`;
  if (d.modo === 'ilimitado') return 'Sin vencimiento (fecha_fin null)';
  return `${d.dias_equivalentes} días (${d.nombre})`;
});

watch(
  () => form.duracion_id,
  () => {
    const d = duracionSeleccionada.value;
    if (!d) return;
    if (d.modo === 'visitas') {
      form.tipo_vencimiento = 'visitas';
      form.cantidad = 1;
      if (!form.limite_asistencias || form.limite_asistencias < 1) form.limite_asistencias = 10;
    } else {
      form.tipo_vencimiento = 'tiempo';
      if (d.modo === 'variable') {
        form.limite_asistencias = null;
        if (!form.cantidad || form.cantidad < 1) form.cantidad = 1;
      } else {
        form.cantidad = 1;
        form.limite_asistencias = null;
      }
    }
  }
);

watch(
  () => props.modelValue,
  (val) => {
    if (val) {
      Object.assign(form, {
        nombre: val.nombre || '',
        descripcion: val.descripcion || '',
        cantidad: val.cantidad || 1,
        duracion_id: val.duracion_id || null,
        tipo_vencimiento: val.tipo_vencimiento || 'tiempo',
        limite_asistencias: val.limite_asistencias ?? null,
        precio: val.precio || 0,
      });
    } else {
      Object.assign(form, {
        nombre: '',
        descripcion: '',
        cantidad: 1,
        duracion_id: null,
        tipo_vencimiento: 'tiempo',
        limite_asistencias: null,
        precio: 0,
      });
    }
  },
  { immediate: true }
);

const cerrar = () => {
  emit('update:visible', false);
  v$.value.$reset();
};

const guardar = async () => {
  v$.value.$touch()
  if (v$.value.$invalid) return

  // Forzado por modo (el backend revalida)
  const payload = {
    ...form,
    ...(esVisitas.value
      ? { cantidad: 1, tipo_vencimiento: 'visitas' }
      : esVariable.value
        ? { tipo_vencimiento: 'tiempo', limite_asistencias: null }
        : { cantidad: 1, tipo_vencimiento: 'tiempo', limite_asistencias: null }),
    ...(props.isEdit ? { id: props.modelValue.id } : {})
  };

  const resultado = await props.onSave(payload)

  if (resultado === true) {
    await Swal.fire('Éxito', 'Membresía guardada correctamente', 'success')
    cerrar()
  } else {
    cerrar()
    await Swal.fire('Advertencia', resultado, 'warning')
   
  }
}


onMounted(async () => {
  try {
    const res = await api.get('/duracion');
    const lista = Array.isArray(res.data) ? res.data : [];
    duraciones.value = lista.filter((d) => d.estado !== false);
    if (duraciones.value.length === 0 && process.env.NODE_ENV !== 'production') {
      console.warn('Sin duraciones activas: crea una en /duraciones');
    }
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') console.error('Error al cargar duraciones:', error);
  }
});

</script>

<template>
  <div class="modal fade" tabindex="-1" :class="{ show: visible }" style="display: block; " v-if="visible">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ isEdit ? 'Editar' : 'Nueva' }} Membresía</h5>
          <button type="button" class="btn-close" @click="cerrar"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="guardar">
            <div class="row">
            
              <div class="mb-3 col-md-6">
                <label class="form-label">Nombre</label>
                <input v-model="form.nombre" type="text" class="form-control form-control-sm"
                  :class="{ 'is-invalid': v$.nombre.$dirty && v$.nombre.$invalid }" @blur="v$.nombre.$touch()" />
                <div class="invalid-feedback" v-if="v$.nombre.$dirty && v$.nombre.$invalid">
                  <div v-if="!v$.nombre.required">El nombre es obligatorio.</div>
                  <div v-else-if="!v$.nombre.minLength">Debe tener al menos 3 caracteres.</div>
                </div>
              </div>

            
              <div class="mb-3 col-md-6">
                <label class="form-label">Descripción</label>
                <input v-model="form.descripcion" type="text" class="form-control form-control-sm"
                  :class="{ 'is-invalid': v$.descripcion.$dirty && v$.descripcion.$invalid }"
                  @blur="v$.descripcion.$touch()" />
                <div class="invalid-feedback" v-if="v$.descripcion.$dirty && v$.descripcion.$invalid">
                  <div v-if="!v$.descripcion.required">La descripción es obligatoria.</div>
                </div>
              </div>

              <div class="mb-3 col-md-6">
                <label class="form-label">Unidad de duración</label>
                <v-select
                  class="vselect-sm"
                  v-model="form.duracion_id"
                  :options="duraciones"
                  label="nombre"
                  :reduce="d => d.id"
                  placeholder="Seleccione duración"
                />
                <div class="invalid-feedback" v-if="v$.duracion_id.$dirty && v$.duracion_id.$invalid">
                  Debe seleccionar una duración.
                </div>
                <small v-if="duraciones.length === 0" class="text-danger">Sin duraciones: crea una en /duraciones.</small>
                <small v-else-if="previewVigencia" class="text-muted d-block mt-1">{{ previewVigencia }}</small>
              </div>

              <div v-if="esVariable" class="mb-3 col-md-6">
                <label class="form-label">Días de temporada (cantidad)</label>
                <input v-model.number="form.cantidad" type="number" min="1" class="form-control form-control-sm"
                  :class="{ 'is-invalid': v$.cantidad.$dirty && v$.cantidad.$invalid }" @blur="v$.cantidad.$touch()" />
                <div class="invalid-feedback" v-if="v$.cantidad.$dirty && v$.cantidad.$invalid">
                  Debe ser mayor a 0.
                </div>
              </div>

              <div class="mb-3 col-md-6">
                <label class="form-label">Tipo de vencimiento</label>
                <select v-model="form.tipo_vencimiento" class="form-select form-select-sm" :disabled="!!duracionSeleccionada">
                  <option value="tiempo">Por tiempo</option>
                  <option value="visitas">Por visitas</option>
                </select>
                <small class="text-muted">Definido por el modo de la duración.</small>
              </div>

              <div v-if="esVisitas" class="mb-3 col-md-6">
                <label class="form-label">Límite de asistencias</label>
                <input v-model.number="form.limite_asistencias" type="number" min="1"
                  class="form-control form-control-sm"
                  :class="{ 'is-invalid': v$.limite_asistencias.$dirty && v$.limite_asistencias.$invalid }" @blur="v$.limite_asistencias.$touch()" />
                <div class="invalid-feedback" v-if="v$.limite_asistencias.$dirty && v$.limite_asistencias.$invalid">
                  Debe ser 1 o mayor.
                </div>
              </div>

             
              <div class="mb-3 col-md-6">
                <label class="form-label">Precio</label>
                <input v-model.number="form.precio" type="number" step="0.01" class="form-control form-control-sm"
                  :class="{ 'is-invalid': v$.precio.$dirty && v$.precio.$invalid }" @blur="v$.precio.$touch()" />
                <div class="invalid-feedback" v-if="v$.precio.$dirty && v$.precio.$invalid">
                  <div v-if="!v$.precio.required">El precio es obligatorio.</div>
                  <div v-else-if="!v$.precio.minValue">Debe ser mayor a 0.</div>
                </div>
              </div>
            </div>

        
            <div class="text-end">
              <button type="button" class="btn btn-secondary btn-sm me-2" @click="cerrar">Cancelar</button>
              <button type="submit" class="btn btn-primary btn-sm">
                {{ isEdit ? 'Actualizar' : 'Guardar' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.modal {
  background: rgba(0, 0, 0, 0.4);
}

.modal-backdrop {
  z-index: 9999 !important;
}

.modal {
  z-index: 9999 !important;
}
.vselect-sm {
  font-size: 12px;
}

.vselect-sm .vs__selected,
.vselect-sm .vs__dropdown-menu li {
  font-size: 12px !important;
}
</style>
